import {
  CAPTURE_ACTION_REGISTRY,
  type CaptureActionSpec,
} from '../captureActionPlan';
import {
  createBrowserActionStripDecoder,
  releaseBrowserActionStripImage,
} from './browserDecoder';
import {
  createActionStripRegistry,
  type ActionArtRole,
  type ActionStripFrameSchedule,
  type ActionStripSources,
} from './registry';
import { createActionArtRuntime, type ActionArtRuntime } from './runtime';
import type { ActionStripDomImageSource } from './domDirector';

const ACTION_BASE = import.meta.env.BASE_URL;

function frameScheduleForRole(role: ActionArtRole): ActionStripFrameSchedule {
  const timings: CaptureActionSpec['timings'] = CAPTURE_ACTION_REGISTRY[role].timings;
  const travelStart = timings.anticipation;
  const contactStart = travelStart + timings.travel;
  const followThroughStart = contactStart + timings.contact;
  const settleStart = followThroughStart + timings['follow-through'] + timings.hold;

  // CapyBoard switches from the authored ready pose to frame 1 at the start of
  // anticipation. Frame 0 therefore shares that first choreography window in
  // metadata; frames 1-5 then mirror the actual phase-to-frame playback map.
  return Object.freeze([
    { frame: 0, startsAtMs: 0, durationMs: timings.anticipation },
    { frame: 1, startsAtMs: 0, durationMs: timings.anticipation },
    { frame: 2, startsAtMs: travelStart, durationMs: timings.travel },
    { frame: 3, startsAtMs: contactStart, durationMs: timings.contact },
    {
      frame: 4,
      startsAtMs: followThroughStart,
      durationMs: timings['follow-through'] + timings.hold,
    },
    { frame: 5, startsAtMs: settleStart, durationMs: timings.settle },
  ]) as ActionStripFrameSchedule;
}

const PAWN_SCHEDULE = frameScheduleForRole('p');
const KNIGHT_SCHEDULE = frameScheduleForRole('n');
const BISHOP_SCHEDULE = frameScheduleForRole('b');
const ROOK_SCHEDULE = frameScheduleForRole('r');
const QUEEN_SCHEDULE = frameScheduleForRole('q');
const KING_SCHEDULE = frameScheduleForRole('k');

/**
 * Release-owned URLs use the exact MD5 content revisions emitted by Workbox.
 * Keep these literals aligned with the installed public files; the production
 * release assertion and the focused source test both reject any byte drift.
 */
export const PRODUCTION_ACTION_STRIP_SOURCES = Object.freeze({
  wp: {
    url: `${ACTION_BASE}action/capychess/white-pawn-capture.webp?v=00a88631baa8`,
    revision: '00a88631baa86a52b571d3683fc86ca1',
    frameSchedule: PAWN_SCHEDULE,
  },
  wn: {
    url: `${ACTION_BASE}action/capychess/white-knight-capture.webp?v=e5e45e3ce927`,
    revision: 'e5e45e3ce927c87ba3ca00875b1d458e',
    frameSchedule: KNIGHT_SCHEDULE,
  },
  wb: {
    url: `${ACTION_BASE}action/capychess/white-bishop-capture.webp?v=e0bf959c9503`,
    revision: 'e0bf959c9503a09dfbc12852803e4797',
    frameSchedule: BISHOP_SCHEDULE,
  },
  wr: {
    url: `${ACTION_BASE}action/capychess/white-rook-capture.webp?v=2fa2c71c5068`,
    revision: '2fa2c71c5068c9408045ebd7c1c56dbd',
    frameSchedule: ROOK_SCHEDULE,
  },
  wq: {
    url: `${ACTION_BASE}action/capychess/white-queen-capture.webp?v=2f510655029f`,
    revision: '2f510655029f85ab2647f246be917348',
    frameSchedule: QUEEN_SCHEDULE,
  },
  wk: {
    url: `${ACTION_BASE}action/capychess/white-king-capture.webp?v=ca0723270c04`,
    revision: 'ca0723270c04c3f554d21f9193164175',
    frameSchedule: KING_SCHEDULE,
  },
  bp: {
    url: `${ACTION_BASE}action/capychess/dark-pawn-capture.webp?v=caef9a326d2d`,
    revision: 'caef9a326d2dd7e1712a63127573a40f',
    frameSchedule: PAWN_SCHEDULE,
  },
  bn: {
    url: `${ACTION_BASE}action/capychess/dark-knight-capture.webp?v=9579437a5015`,
    revision: '9579437a5015aa9f14cd4252b940f0ea',
    frameSchedule: KNIGHT_SCHEDULE,
  },
  bb: {
    url: `${ACTION_BASE}action/capychess/dark-bishop-capture.webp?v=7e9cdb8557fc`,
    revision: '7e9cdb8557fc5570f212cd7728342afb',
    frameSchedule: BISHOP_SCHEDULE,
  },
  br: {
    url: `${ACTION_BASE}action/capychess/dark-rook-capture.webp?v=507e1648105c`,
    revision: '507e1648105cb972e6bb7d05edbfcf22',
    frameSchedule: ROOK_SCHEDULE,
  },
  bq: {
    url: `${ACTION_BASE}action/capychess/dark-queen-capture.webp?v=c4f77ce387b2`,
    revision: 'c4f77ce387b2eaf4e8637c8b9f4ddd75',
    frameSchedule: QUEEN_SCHEDULE,
  },
  bk: {
    url: `${ACTION_BASE}action/capychess/dark-king-capture.webp?v=a981f02fdae6`,
    revision: 'a981f02fdae600a121a5b0a299bd92c9',
    frameSchedule: KING_SCHEDULE,
  },
}) satisfies ActionStripSources;

export const PRODUCTION_ACTION_STRIP_REGISTRY = createActionStripRegistry(
  PRODUCTION_ACTION_STRIP_SOURCES,
);

/** Creates the lazy browser runtime. Construction itself performs no decode. */
export function createProductionActionArtRuntime(): ActionArtRuntime<ActionStripDomImageSource> {
  return createActionArtRuntime({
    registry: PRODUCTION_ACTION_STRIP_REGISTRY,
    decoder: createBrowserActionStripDecoder(),
    releaseImage: releaseBrowserActionStripImage,
  });
}
